import { useNotifications } from '@/hooks/useNotification';
import { markAsRead } from '@/services/api/notification';
import { INotification } from '@/types/api/notification';
import {
  Divider,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ScrollShadow,
  Skeleton,
} from '@nextui-org/react';
import moment from 'moment';
import Link from 'next/link';
import React, { useCallback, useState } from 'react';
import PaginationWithPageSize from '../PaginationWithPageSize';

interface NotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const NotificationItem = ({
  notification,
  onClose,
  refetch,
}: {
  notification: INotification;
  onClose: () => void;
  refetch: () => void;
}) => {
  const handleNotificationClick = useCallback(
    async (e: React.MouseEvent) => {
      if (!notification.isRead) {
        await markAsRead(notification.id);
      }

      const idMatch = notification.content.match(/ID:\s*(\d+)/i);

      if (idMatch && idMatch[1]) {
        const candidateId = idMatch[1];

        if (notification.url.includes('/job/')) {
          e.preventDefault();
          try {
            const fullUrl = notification.url.startsWith('http') ? notification.url : ``;
            const urlObj = new URL(fullUrl);

            urlObj.searchParams.set('candidateId', candidateId);

            let newUrl = urlObj.pathname + urlObj.search;

            if (!notification.url.startsWith('http')) {
              newUrl = handleFormatUrl(newUrl);
            } else {
              newUrl = urlObj.toString();
            }

            window.location.href = handleFormatUrl(newUrl);
            onClose();
            refetch();
            return;
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error adding candidateId to URL:', error);
          }
        }
      }

      onClose();
      refetch();
    },
    [notification, onClose, refetch],
  );

  const handleFormatUrl = (url: string) => {
    try {
      const localOrigin = location.origin;
      const isLocalhost = localOrigin?.includes('localhost');

      if (!isLocalhost) return url;

      let fullUrl = url;

      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        fullUrl = url.startsWith('/') ? `${localOrigin}${url}` : `${localOrigin}/${url}`;
      }

      const urlObj = new URL(fullUrl);

      return localOrigin + urlObj.pathname + urlObj.search;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Invalid URL:', url);
      return url;
    }
  };

  return (
    <Link
      href={handleFormatUrl(notification.url)}
      className='flex gap-3 py-3 items-start'
      onClick={handleNotificationClick}
    >
      <div
        className={`w-2 h-2 rounded-full mt-2 ${
          notification.isRead ? 'bg-gray-300' : 'bg-blue-400'
        }`}
      />
      <div className='flex-1'>
        <p className='font-bold'>{notification.title}</p>
        <p className='text-sm text-gray-500 whitespace-pre-line'>{notification.content}</p>
      </div>
      <span className='text-xs text-gray-400 whitespace-nowrap'>
        {moment(notification.createdAt).fromNow()}
      </span>
    </Link>
  );
};

const NotificationModal = ({ isOpen, onClose }: NotificationModalProps) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const { data: notifications, isLoading, refetch } = useNotifications({ page, pageSize });

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const handleChangePageSize = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  const renderSkeletons = () => {
    return Array(5)
      .fill(0)
      .map((_, index) => (
        <div key={index}>
          <div className='flex gap-3 py-3 items-start'>
            <div className='w-2 h-2 rounded-full mt-2'>
              <Skeleton className='w-2 h-2 rounded-full' />
            </div>
            <div className='flex-1'>
              <Skeleton className='w-1/3 h-6 rounded-lg mb-2' />
              <Skeleton className='w-full h-4 rounded-lg' />
              <Skeleton className='w-full h-4 rounded-lg mt-1' />
            </div>
            <Skeleton className='w-20 h-4 rounded-lg' />
          </div>
          {index < 2 && <Divider />}
        </div>
      ));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      placement='center'
      size='2xl'
      key={`notification-modal-${page}-${pageSize}`}
    >
      <ModalContent className=''>
        {(onClose) => (
          <>
            <ModalHeader className='flex flex-col gap-1'>Notification</ModalHeader>
            <ModalBody className='p-0'>
              <ScrollShadow hideScrollBar className='h-[500px] px-6'>
                {isLoading ? (
                  renderSkeletons()
                ) : (
                  <>
                    {notifications?.items?.map((notification: INotification, index: number) => (
                      <React.Fragment key={notification.id}>
                        <NotificationItem
                          notification={notification}
                          onClose={onClose}
                          refetch={refetch}
                        />
                        {index < (notifications?.items?.length ?? 0) - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                    {notifications?.items?.length === 0 && (
                      <p className='py-4 text-center text-gray-500'>No notifications</p>
                    )}
                  </>
                )}
              </ScrollShadow>
              <PaginationWithPageSize
                size='sm'
                meta={notifications?.meta}
                onChangePage={handleChangePage}
                onChangePageSize={handleChangePageSize}
              />
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default NotificationModal;
